import { useEffect, useState } from 'react';
import { useT } from '../i18n/useT';
import { formatNumber } from '../lib/format';
import { nativeVault } from '../lib/folderSync';
import { requestPersistentStorage, storageHealth, type StorageHealth } from '../lib/storageHealth';

/** Bytes as MB, which is the scale a library of notes lives at. */
function megabytes(bytes: number): string {
  return `${formatNumber(Math.max(1, Math.round(bytes / 1_000_000)))} MB`;
}

/**
 * Where the library is kept, and whether the browser promises to keep it.
 *
 * Worth a settings row of its own: "it is in your browser" is only reassuring
 * while the browser is not short of space. In a native build the answer is
 * different and better — the notes are files — so that is what it says there.
 */
export function StorageSettings() {
  const t = useT();
  const native = nativeVault();
  const [health, setHealth] = useState<StorageHealth | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    void storageHealth().then(setHealth);
  }, []);

  const used =
    health?.usage != null
      ? t('storage.used', {
          used: megabytes(health.usage),
          quota: health.quota != null ? megabytes(health.quota) : '—',
        })
      : null;

  return (
    <div className="mb-setting mb-setting-block">
      <label>{t('storage.title')}</label>
      <div className="mb-backup">
        {native ? (
          <p className="mb-modal-note">{t('storage.native', { place: native.label })}</p>
        ) : (
          <p className="mb-modal-note">{t('storage.browser')}</p>
        )}

        {health && !native && (
          <div className="mb-backup-row">
            <span className="mb-backup-folder">
              {!health.supported
                ? t('storage.unknown')
                : health.persisted
                  ? t('storage.persisted')
                  : t('storage.evictable')}
            </span>
            {health.supported && !health.persisted && (
              <button
                className="mb-btn"
                disabled={busy}
                onClick={() =>
                  void (async () => {
                    setBusy(true);
                    try {
                      await requestPersistentStorage();
                      setHealth(await storageHealth());
                    } finally {
                      setBusy(false);
                    }
                  })()
                }
              >
                {t('storage.keep')}
              </button>
            )}
          </div>
        )}

        {used && <p className="mb-backup-last">{used}</p>}
      </div>
    </div>
  );
}
